/**
 * U7 (refactoring program 2026-11): the one rule for showing a person's name.
 *
 * The API answers `null` for someone with neither a display name nor a full
 * name (it used to send "Unknown" in the ranking and the gallery, and `''` or
 * nothing elsewhere). The app shows "Membro sem nome" for all of them, and an
 * avatar without a name shows "?" — never "null", "Unknown" or an empty line.
 */

/** Any `t` (react-i18next's, or a narrower one a component passes down). */
type Translate = (key: string) => string

/** What the API has sent in place of a name. "Unknown" is the pre-U7 ranking and gallery. */
const PLACEHOLDERS = new Set(['unknown', 'null', 'undefined'])

/** The first real name among the candidates, in order, or `null` when there is none. */
export const pickPersonName = (...candidates: unknown[]): string | null => {
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue
    const value = candidate.trim()
    if (value && !PLACEHOLDERS.has(value.toLowerCase())) return value
  }
  return null
}

/** The shapes the API uses for a person: `formatProfile` (`name`) and the raw profile (`displayName`/`fullName`). */
export interface PersonNameSource {
  displayName?: string | null
  name?: string | null
  fullName?: string | null
}

/** A person's real name (display name, then the API's `name`, then full name), or `null`. Use it for avatar initials. */
export const nameOf = (person: PersonNameSource | null | undefined): string | null =>
  person ? pickPersonName(person.displayName, person.name, person.fullName) : null

/** The text to show: the person's name, or "Membro sem nome" in the reader's language. */
export const personName = (t: Translate, person: PersonNameSource | null | undefined): string =>
  nameOf(person) ?? t('common:unnamedMember')

/** Same as `personName`, for a name already picked from loose fields. */
export const displayPersonName = (t: Translate, ...candidates: unknown[]): string =>
  pickPersonName(...candidates) ?? t('common:unnamedMember')
